import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import SearchIcon from '@mui/icons-material/Search'

import API from '../../component/Axios'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import Title from '../../component/Title'
import {
    LIST_FILTER_DATE_FIELD_SX,
    LIST_FILTER_DIVIDER_SX,
    ListFilterBar,
    ListFilterForm,
} from '../../component/ListFilterLayout'

const MAX_DATE_RANGE_DAYS = 366
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

const STATUS_CONFIG = {
    ENABLED: { label: '운영 중', color: 'success' },
    PAUSED: { label: '일시중지', color: 'warning' },
    REMOVED: { label: '삭제', color: 'default', variant: 'outlined' },
    UNKNOWN: { label: '알 수 없음', color: 'default', variant: 'outlined' },
    UNSPECIFIED: { label: '미지정', color: 'default', variant: 'outlined' },
}

const CHANNEL_LABELS = {
    APP: '앱',
    DEMAND_GEN: '디맨드젠',
    DISPLAY: '디스플레이',
    HOTEL: '호텔',
    LOCAL: '지역',
    LOCAL_SERVICES: '지역 서비스',
    MULTI_CHANNEL: '멀티채널',
    PERFORMANCE_MAX: '실적 최대화',
    SEARCH: '검색',
    SHOPPING: '쇼핑',
    SMART: '스마트',
    TRAVEL: '여행',
    VIDEO: '동영상',
}

const NUMERIC_SORT_KEYS = new Set([
    'impressions',
    'clicks',
    'ctrPercent',
    'cost',
    'averageCpc',
    'installCount',
    'signupCount',
    'costPerSignup',
    'estimatedInstallSignupRatePercent',
])

const COLUMN_TOOLTIPS = {
    status: 'Google Ads 캠페인의 현재 운영 상태입니다.',
    campaign: 'Google Ads 캠페인명과 캠페인 ID입니다.',
    channelType: '검색, 디스플레이, 앱 등 Google Ads의 광고 유형입니다.',
    impressions: '조회 기간 동안 광고가 사용자 화면에 표시된 횟수입니다.',
    clicks: '조회 기간 동안 광고가 클릭된 횟수입니다.',
    ctr: '클릭률(CTR)입니다. 클릭수 ÷ 노출수 × 100으로 계산합니다.',
    cost: '조회 기간 동안 Google Ads에서 발생한 총 광고 비용입니다.',
    averageCpc: '클릭 1회당 평균 비용입니다. 총 광고 비용 ÷ 클릭수로 계산합니다.',
    installs: 'Google Ads가 랭킹마라톤 Android·iOS 설치 전환으로 집계한 수입니다.',
    appSignups: '조회 기간에 캠페인 타겟 국가코드와 같은 국가코드로 가입한 앱 회원 수입니다. 광고 유입 귀속 수치는 아니며 동일 국가 캠페인에는 같은 값이 표시됩니다.',
    costPerSignup: '총 광고 비용 ÷ 앱 회원가입 수로 계산하며 소수점은 버립니다. 광고 귀속이 아닌 동일 국가 가입자 기준의 참고값이며, 앱 회원가입이 없으면 표시하지 않습니다.',
    estimatedInstallSignupRate: '동일 국가 앱 회원가입 수 ÷ 광고 설치수 × 100으로 계산한 참고용 추정치입니다. 회원가입은 광고에 직접 귀속되지 않아 100%를 넘을 수 있습니다.',
}

const CAMPAIGN_COLUMNS = [
    { sortKey: 'status', label: '상태', tooltipKey: 'status', width: 100 },
    { sortKey: 'campaignName', label: '캠페인', tooltipKey: 'campaign', width: 230 },
    { sortKey: 'channelType', label: '유형', tooltipKey: 'channelType', width: 100, align: 'right' },
    { sortKey: 'impressions', label: '노출수', tooltipKey: 'impressions', width: 100, align: 'right' },
    { sortKey: 'clicks', label: '클릭수', tooltipKey: 'clicks', width: 100, align: 'right' },
    { sortKey: 'ctrPercent', label: 'CTR', tooltipKey: 'ctr', width: 100, align: 'right' },
    { sortKey: 'cost', label: '비용', tooltipKey: 'cost', width: 120, align: 'right' },
    { sortKey: 'averageCpc', label: '평균 CPC', tooltipKey: 'averageCpc', width: 120, align: 'right' },
    { sortKey: 'installCount', label: '광고 설치', tooltipKey: 'installs', width: 120, align: 'right' },
    { sortKey: 'signupCount', label: '앱 회원가입', tooltipKey: 'appSignups', width: 130, align: 'right' },
    { sortKey: 'costPerSignup', label: '1인 가입비용', tooltipKey: 'costPerSignup', width: 130, align: 'right' },
    { sortKey: 'estimatedInstallSignupRatePercent', label: '추정 가입률', tooltipKey: 'estimatedInstallSignupRate', width: 130, align: 'right' },
]

const CAMPAIGN_TABLE_MIN_WIDTH = CAMPAIGN_COLUMNS.reduce((total, column) => (
    total + column.width
), 0)

function ColumnHeader({ children, description, active, direction, onClick }) {
    return (
        <Tooltip title={description} arrow>
            <TableSortLabel
                active={active}
                direction={active ? direction : 'asc'}
                onClick={onClick}
            >
                <Box
                    component="span"
                    sx={{
                        borderBottom: '1px dotted',
                        borderColor: 'text.secondary',
                    }}
                >
                    {children}
                </Box>
            </TableSortLabel>
        </Tooltip>
    )
}

function SortableTableCell({
    align = 'left',
    children,
    description,
    onSort,
    sortConfig,
    sortKey,
    width,
}) {
    const active = sortConfig.key === sortKey

    return (
        <TableCell
            align={align}
            sortDirection={active ? sortConfig.direction : false}
            sx={{ width }}
        >
            <ColumnHeader
                description={description}
                active={active}
                direction={sortConfig.direction}
                onClick={() => onSort(sortKey)}
            >
                {children}
            </ColumnHeader>
        </TableCell>
    )
}

const createEmptyReport = () => ({
    customerId: '',
    currencyCode: 'KRW',
    timeZone: '',
    range: null,
    summary: {
        campaignCount: 0,
        impressions: 0,
        clicks: 0,
        cost: 0,
        installCount: 0,
        ctrPercent: 0,
        averageCpc: 0,
        totalSignupCount: 0,
    },
    campaigns: [],
    fetchedAt: '',
})

const formatUtcDate = (date) => {
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

const getTodayInSeoul = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Seoul',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date())

    const year = parts.find((part) => part.type === 'year')?.value
    const month = parts.find((part) => part.type === 'month')?.value
    const day = parts.find((part) => part.type === 'day')?.value

    return `${year}-${month}-${day}`
}

const createInitialRange = () => {
    const endDate = getTodayInSeoul()
    const startDate = new Date(`${endDate}T00:00:00Z`)
    startDate.setUTCDate(startDate.getUTCDate() - 29)

    return {
        startDate: formatUtcDate(startDate),
        endDate,
    }
}

const getInclusiveDateRangeDays = (startDate, endDate) => {
    const startTime = Date.parse(`${startDate}T00:00:00Z`)
    const endTime = Date.parse(`${endDate}T00:00:00Z`)

    return Math.floor((endTime - startTime) / MILLISECONDS_PER_DAY) + 1
}

const formatInteger = (value) => new Intl.NumberFormat('ko-KR', {
    maximumFractionDigits: 0,
}).format(Number(value) || 0)

const formatDecimal = (value) => new Intl.NumberFormat('ko-KR', {
    maximumFractionDigits: 2,
}).format(Number(value) || 0)

const formatPercent = (value) => `${formatDecimal(value)}%`

const formatNullablePercent = (value) => (
    value === null || value === undefined ? '-' : formatPercent(value)
)

const formatCountryCodes = (countryCodes) => (
    Array.isArray(countryCodes) && countryCodes.length > 0
        ? countryCodes.join(', ')
        : '-'
)

const getCampaignSortValue = (campaign, sortKey) => {
    if (sortKey === 'status') {
        return STATUS_CONFIG[campaign.status]?.label || campaign.status || null
    }

    if (sortKey === 'campaignName') {
        return campaign.campaignName || null
    }

    if (sortKey === 'channelType') {
        return CHANNEL_LABELS[campaign.channelType] || campaign.channelType || null
    }

    const value = campaign[sortKey]

    if (value === null || value === undefined || value === '') {
        return null
    }

    if (!NUMERIC_SORT_KEYS.has(sortKey)) {
        return String(value)
    }

    const numberValue = Number(value)

    return Number.isFinite(numberValue) ? numberValue : null
}

const compareCampaigns = (left, right, sortKey, direction) => {
    const leftValue = getCampaignSortValue(left, sortKey)
    const rightValue = getCampaignSortValue(right, sortKey)

    if (leftValue === null && rightValue !== null) {
        return 1
    }

    if (leftValue !== null && rightValue === null) {
        return -1
    }

    let comparison = 0

    if (leftValue !== null && rightValue !== null) {
        comparison = NUMERIC_SORT_KEYS.has(sortKey)
            ? leftValue - rightValue
            : String(leftValue).localeCompare(String(rightValue), 'ko', {
                numeric: true,
                sensitivity: 'base',
            })
    }

    if (comparison === 0) {
        return String(left.campaignId || '').localeCompare(String(right.campaignId || ''), 'en', {
            numeric: true,
        })
    }

    return direction === 'desc' ? -comparison : comparison
}

const formatCurrency = (value, currencyCode) => {
    const currency = currencyCode || 'KRW'

    try {
        return new Intl.NumberFormat('ko-KR', {
            style: 'currency',
            currency,
            maximumFractionDigits: currency === 'KRW' ? 0 : 2,
        }).format(Number(value) || 0)
    } catch (_error) {
        return `${formatDecimal(value)} ${currency}`
    }
}

const formatNullableCurrency = (value, currencyCode) => (
    value === null || value === undefined ? '-' : formatCurrency(value, currencyCode)
)

const formatCustomerId = (customerId) => {
    const value = String(customerId || '')

    if (!/^\d{10}$/.test(value)) {
        return value || '-'
    }

    return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`
}

const formatFetchedAt = (value) => {
    const date = new Date(value)

    if (!value || Number.isNaN(date.getTime())) {
        return '-'
    }

    return new Intl.DateTimeFormat('ko-KR', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(date)
}

export default function GoogleAds({ adminId, role }) {
    const history = useHistory()
    const initialRangeRef = useRef(createInitialRange())
    const requestSequenceRef = useRef(0)
    const [loading, setLoading] = useState(true)
    const [report, setReport] = useState(createEmptyReport)
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
    const [startDate, setStartDate] = useState(initialRangeRef.current.startDate)
    const [endDate, setEndDate] = useState(initialRangeRef.current.endDate)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('error')
    const [messageSnackbar, setMessageSnackbar] = useState('')

    const showError = useCallback((message) => {
        setSeveritySnackbar('error')
        setMessageSnackbar(message)
        setOpenSnackbar(true)
    }, [])

    const getData = useCallback(async (nextStartDate, nextEndDate) => {
        if (!nextStartDate || !nextEndDate) {
            showError('시작 날짜와 종료 날짜를 선택하세요.')
            return
        }

        const rangeDays = getInclusiveDateRangeDays(nextStartDate, nextEndDate)
        const today = getTodayInSeoul()

        if (rangeDays < 1) {
            showError('시작 날짜는 종료 날짜보다 늦을 수 없습니다.')
            return
        }

        if (rangeDays > MAX_DATE_RANGE_DAYS) {
            showError('조회 기간은 최대 366일까지 선택할 수 있습니다.')
            return
        }

        if (nextEndDate > today) {
            showError('종료 날짜는 오늘보다 늦을 수 없습니다.')
            return
        }

        const requestId = requestSequenceRef.current + 1
        requestSequenceRef.current = requestId
        setLoading(true)

        try {
            const { data: response } = await API.post('/api/v1/admin/googleAds/getCampaignReport', {
                startDate: nextStartDate,
                endDate: nextEndDate,
            })

            if (requestId !== requestSequenceRef.current) {
                return
            }

            if (Number(response.code) === 200) {
                setReport({
                    ...createEmptyReport(),
                    ...(response.data || {}),
                    summary: {
                        ...createEmptyReport().summary,
                        ...(response.data?.summary || {}),
                    },
                    campaigns: Array.isArray(response.data?.campaigns)
                        ? response.data.campaigns
                        : [],
                })
            } else if (Number(response.code) === 403) {
                history.push('/login')
            } else {
                showError(response.message || 'Google Ads 데이터를 조회하지 못했습니다.')
            }
        } catch (error) {
            if (requestId !== requestSequenceRef.current) {
                return
            }

            showError(
                error.response?.data?.message
                || error.message
                || 'Google Ads 데이터를 조회하지 못했습니다.'
            )
        } finally {
            if (requestId === requestSequenceRef.current) {
                setLoading(false)
            }
        }
    }, [history, showError])

    useEffect(() => {
        if (adminId && role >= 4) {
            getData(initialRangeRef.current.startDate, initialRangeRef.current.endDate)
        }
    }, [adminId, getData, role])

    const summaryItems = useMemo(() => {
        const summary = report.summary || {}

        return [
            { key: 'campaignCount', label: '캠페인', value: formatInteger(summary.campaignCount) },
            { key: 'impressions', label: '노출수', value: formatInteger(summary.impressions) },
            { key: 'clicks', label: '클릭수', value: formatInteger(summary.clicks) },
            { key: 'ctrPercent', label: 'CTR', value: formatPercent(summary.ctrPercent) },
            { key: 'cost', label: '비용', value: formatCurrency(summary.cost, report.currencyCode) },
            { key: 'averageCpc', label: '평균 CPC', value: formatCurrency(summary.averageCpc, report.currencyCode) },
            { key: 'installCount', label: '광고 설치', value: formatDecimal(summary.installCount) },
            { key: 'totalSignupCount', label: '전체 앱 회원가입', value: formatInteger(summary.totalSignupCount) },
        ]
    }, [report.currencyCode, report.summary])

    const sortedCampaigns = useMemo(() => {
        if (!sortConfig.key) {
            return report.campaigns
        }

        return [...report.campaigns].sort((left, right) => (
            compareCampaigns(left, right, sortConfig.key, sortConfig.direction)
        ))
    }, [report.campaigns, sortConfig])

    const handleSort = useCallback((key) => {
        setSortConfig((current) => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
        }))
    }, [])

    const handleSubmit = (event) => {
        event.preventDefault()
        getData(startDate, endDate)
    }

    if (role < 4) {
        return null
    }

    return (
        <>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1.5,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Title sx={{ mb: 0.5 }}>Google Ads 캠페인 성과</Title>
                            <Typography variant="body2" color="text.secondary">
                                광고 계정 {formatCustomerId(report.customerId)}
                                {report.range?.startDate && ` | ${report.range.startDate} ~ ${report.range.endDate}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                최근 조회 {formatFetchedAt(report.fetchedAt)}
                            </Typography>
                        </Box>
                        <ListFilterBar sx={{ width: 'auto', justifyContent: 'flex-end', mt: 0, mb: 0 }}>
                            <ListFilterForm onSubmit={handleSubmit}>
                                <TextField
                                    required
                                    label="시작 날짜"
                                    type="date"
                                    size="small"
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                    inputProps={{ max: endDate }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={LIST_FILTER_DATE_FIELD_SX}
                                />
                                <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                                <TextField
                                    required
                                    label="종료 날짜"
                                    type="date"
                                    size="small"
                                    value={endDate}
                                    onChange={(event) => setEndDate(event.target.value)}
                                    inputProps={{ min: startDate, max: getTodayInSeoul() }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={LIST_FILTER_DATE_FIELD_SX}
                                />
                                <Tooltip title="조회">
                                    <span>
                                        <IconButton type="submit" aria-label="Google Ads 조회" disabled={loading}>
                                            <SearchIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </ListFilterForm>
                        </ListFilterBar>
                    </Box>

                    {loading ? (
                        <Spiner />
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))',
                                    mt: 2,
                                    mb: 0.75,
                                    borderTop: '1px solid',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {summaryItems.map((item) => (
                                    <Box
                                        key={item.key}
                                        sx={{
                                            minWidth: 0,
                                            px: 1.5,
                                            py: 1.25,
                                            borderRight: '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary">
                                            {item.label}
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                mt: 0.25,
                                                fontWeight: 700,
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {item.value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                                전체 앱 회원가입은 조회 기간 내 테스트 계정을 제외한 앱 가입자 수입니다.
                                캠페인 행은 광고 귀속이 아닌 타겟 국가코드가 같은 앱 가입자 수이며,
                                동일 국가를 타겟하는 캠페인에는 같은 값이 표시됩니다.
                                추정 가입률은 같은 국가 앱 회원가입 수를 광고 설치수로 나눈 참고값입니다.
                            </Typography>

                            <ListTableContainer>
                                <Table
                                    size="small"
                                    sx={{ minWidth: CAMPAIGN_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {CAMPAIGN_COLUMNS.map((column) => (
                                                <SortableTableCell
                                                    key={column.sortKey}
                                                    align={column.align}
                                                    description={COLUMN_TOOLTIPS[column.tooltipKey]}
                                                    onSort={handleSort}
                                                    sortConfig={sortConfig}
                                                    sortKey={column.sortKey}
                                                    width={column.width}
                                                >
                                                    {column.label}
                                                </SortableTableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {report.campaigns.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={CAMPAIGN_COLUMNS.length}>
                                                    <ListEmptyState
                                                        title="조회된 캠페인 데이터가 없습니다."
                                                        description="조회 기간을 변경한 뒤 다시 확인해보세요."
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ) : sortedCampaigns.map((campaign) => (
                                            <TableRow key={campaign.campaignId} hover>
                                                <TableCell>
                                                    <StatusChip
                                                        status={campaign.status}
                                                        config={STATUS_CONFIG}
                                                        fallbackLabel={campaign.status || '-'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}
                                                    >
                                                        {campaign.campaignName || '-'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {campaign.campaignId || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {CHANNEL_LABELS[campaign.channelType] || campaign.channelType || '-'}
                                                </TableCell>
                                                <TableCell align="right">{formatInteger(campaign.impressions)}</TableCell>
                                                <TableCell align="right">{formatInteger(campaign.clicks)}</TableCell>
                                                <TableCell align="right">{formatPercent(campaign.ctrPercent)}</TableCell>
                                                <TableCell align="right">{formatCurrency(campaign.cost, report.currencyCode)}</TableCell>
                                                <TableCell align="right">{formatCurrency(campaign.averageCpc, report.currencyCode)}</TableCell>
                                                <TableCell align="right">{formatDecimal(campaign.installCount)}</TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {formatInteger(campaign.signupCount)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatCountryCodes(campaign.countryCodes)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNullableCurrency(
                                                        campaign.costPerSignup,
                                                        report.currencyCode
                                                    )}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatNullablePercent(
                                                        campaign.estimatedInstallSignupRatePercent
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </ListTableContainer>
                        </>
                    )}
                </Paper>
            </Grid>
            {openSnackbar && (
                <Snackbars
                    open={openSnackbar}
                    severity={severitySnackbar}
                    message={messageSnackbar}
                    handleClose={() => setOpenSnackbar(false)}
                />
            )}
        </>
    )
}
